import { useState } from 'react'
import { LogOut } from 'lucide-react'
import { fetchDemoWorkers, searchDemoWorkers, useDemoPeople, useMe, useOpenDemoSession } from '../index.js'
import { ApiError } from '../../core/index.js'
import { adoptDemoSession, getDemoSessionInfo, returnToOpener, type DemoSessionInfo } from '../demoSession.js'
import { DemoPeopleModal, DemoRoleButton } from './DemoPeopleModal.js'
import { DemoSessionExpiry } from './DemoSessionExpiry.js'
import { FacilityAdministratorIcon, OperatorIcon, WorkerIcon } from '../../core/index.js'
import { Button } from '../../core/index.js'

/** A demo session an admin opened records its opener as `admin:<username>`. */
const ADMIN_OPENER_PREFIX = 'admin:'

const ROLE_WORD = { operator: 'operator', facility_administrator: 'facility administrator', worker: 'worker' } as const

function endsAt(iso: string | null): string | null {
  if (!iso) {
    return null
  }
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? null : date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

/**
 * Under the header of a shell opened as one of a demo client's people:
 * who this session is, that it is a demo session, when it ends, a switch
 * to become someone else in the same tab, and a button that ends it. The API's word
 * (`/auth/me`) decides whether the banner shows, and the API's list decides
 * who the switch offers: anyone the session's opener could have opened,
 * until the session's original end. The opener is on hover rather than on
 * screen, since a prospect may be watching.
 */
export function DemoSessionBanner() {
  const meQuery = useMe()
  const [isChoosing, setIsChoosing] = useState(false)
  // Held in state so an extension moves the end shown without a reload.
  const [info, setInfo] = useState<DemoSessionInfo | null>(getDemoSessionInfo)
  const demoSession = meQuery.data?.demoSession ?? null
  const peopleQuery = useDemoPeople(demoSession !== null && isChoosing)
  const open = useOpenDemoSession()

  if (!demoSession) {
    return null
  }
  const role = info?.role ?? 'operator'
  const Icon = role === 'worker' ? WorkerIcon : role === 'facility_administrator' ? FacilityAdministratorIcon : OperatorIcon
  const who = info?.name ?? info?.username ?? (`${meQuery.data?.firstName ?? ''} ${meQuery.data?.lastName ?? ''}`.trim() || 'this person')
  const expiresAt = info?.expiresAt ?? demoSession.expiresAt
  const ends = endsAt(expiresAt)
  const openerIsAdmin = demoSession.openedBy.startsWith(ADMIN_OPENER_PREFIX)
  const switchError = open.error instanceof ApiError ? open.error.message : open.isError ? 'Could not switch. Try again in a moment.' : null

  return (
    <div
      role="status"
      title={`Opened by ${demoSession.openedBy}.`}
      className="bg-gold/20 border-b border-gold/60 px-4 py-1.5 text-[11px] text-ink flex flex-wrap items-center justify-center gap-x-3 gap-y-1"
    >
      <span className="inline-flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 text-ink-mid" />
        <span>
          <strong>Demo session</strong> as {who} ({ROLE_WORD[role]})
          {ends && <span className="text-ink-mid"> · ends {ends}</span>}
        </span>
      </span>
      {/* A session the client's own staff opened switches only while the admin has View as on for them; an admin's always does. */}
      {meQuery.data?.viewAs !== false && <DemoRoleButton label="Switch" tone="banner" showLabel onClick={() => setIsChoosing(true)} />}
      {/* The same way out the chooser's "Your account" row gives, without opening the chooser: closes this tab and returns to whoever opened the session. */}
      <Button icon={LogOut} size="sm" title="End this demo session and return to the account that opened it" onClick={() => returnToOpener(openerIsAdmin)}>
        End session
      </Button>
      <DemoSessionExpiry expiresAt={expiresAt} openerIsAdmin={openerIsAdmin} onExtended={(next) => next && setInfo(next)} />
      {isChoosing && (
        <DemoPeopleModal
          title="Switch this demo session to…"
          subtitle="This tab becomes the person you choose, until the session's original end. Your own account closes this tab and returns to where you opened it."
          people={peopleQuery.data}
          loadWorkers={fetchDemoWorkers}
          searchWorkers={searchDemoWorkers}
          workersKey="own-session"
          isLoading={peopleQuery.isPending}
          loadError={peopleQuery.isError ? (peopleQuery.error instanceof ApiError ? peopleQuery.error.message : 'Could not load the people.') : null}
          current={info ? { role: info.role, subject: info.subject } : null}
          onChoose={(input) => open.mutate(input, { onSuccess: adoptDemoSession })}
          onReturnToOpener={() => {
            const openerIsAdmin = peopleQuery.data?.opener?.kind === 'root' || peopleQuery.data?.opener?.kind === 'admin'
            // The chooser goes first, so nothing of the demo session is left on screen while the tab closes.
            setIsChoosing(false)
            returnToOpener(openerIsAdmin)
          }}
          pending={open.isPending || open.isSuccess}
          error={switchError}
          onClose={() => setIsChoosing(false)}
        />
      )}
    </div>
  )
}
